'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DateTime } from 'luxon'
import { Inventory, SailPackage, SailRef, VoyageInfo, Locale } from '@/root/shared-types'
import { modify } from '@/app/actions/reservation-modification'
import { buildUpdateSailPackagesModification } from '@/app/actions/modification-helpers'
import { getModifiedInventory, getTravelDuration } from '@/root/modules/shared/SelectDateTimePage'
import { checkIsDayDisabled } from '@/root/shared-helpers'
import { defaultInventory } from '@/root/shared-consts'
import { MODIFY_ERROR_CODES } from '@/root/shared-types/api/reservation'
import { useInventoryForDate } from './useInventoryForDate'
import { MODIFY_ERROR_MESSAGE_KEYS } from '../consts'
import { countReservationItems, formatActionError, handleStaleServerAction, hasSailPackageChanged } from '../utils'
import type { TripDirection } from '../types'
import { ReservationWithModification } from '../types'
import type { Key } from '@/root/shared-hooks/useGetTranslations/useGetTranslations'

export interface RouteDirectionState {
  date: Date
  time: string
  sailRefId: number
  routeInfo: VoyageInfo
  modifiedInventory: Inventory
  isInventoryLoading: boolean
  isDayDisabled: (day: Date) => boolean
  onDayClick: (date: Date) => void
  setRefIdAndTime: (sailRefId: number, time: string, isNextDay: boolean) => void
  isSaved: boolean
}

export interface UseRouteEditStateReturn {
  outbound: RouteDirectionState
  return: RouteDirectionState | null
  isSaving: boolean
  savingRoute: TripDirection | null
  hasRouteChanges: boolean
  outboundSaved: boolean
  returnSaved: boolean
  saveRouteChanges: () => Promise<ReservationWithModification | null>
  resetToOriginal: () => void
  ticketSailRefId: number | undefined
  returnTicketSailRefId: number | undefined
}

interface UseRouteEditStateParams {
  currentReservation: ReservationWithModification
  reservationToken?: string
  locale: Locale
  firstSailPackage: SailPackage | undefined
  firstSailRef: SailRef | undefined
  secondSailPackage: SailPackage | undefined
  secondSailRef: SailRef | undefined
  committedSailPackages: SailPackage[] | undefined
  fallbackTicketSailRefId: number | undefined
  availableDatesOutbound: Date[]
  availableDatesReturn: Date[]
  getTranslation: (key: Key) => string
  onReservationUpdated: (updated: ReservationWithModification) => void
  onError: (message: string | null) => void
}

export function useRouteEditState({
  currentReservation,
  reservationToken,
  locale,
  firstSailPackage,
  firstSailRef,
  secondSailPackage,
  secondSailRef,
  committedSailPackages,
  fallbackTicketSailRefId,
  availableDatesOutbound,
  availableDatesReturn,
  getTranslation,
  onReservationUpdated,
  onError,
}: UseRouteEditStateParams): UseRouteEditStateReturn {
  // Outbound state
  const [outboundDate, setOutboundDate] = useState<Date>(
    firstSailRef?.departureDate ? DateTime.fromISO(firstSailRef.departureDate).toJSDate() : new Date()
  )
  const [outboundSailRefId, setOutboundSailRefId] = useState(firstSailRef?.sailRefId ?? 0)
  const [outboundTime, setOutboundTime] = useState(firstSailRef?.departureTime ?? '')

  // Return state
  const [returnDate, setReturnDate] = useState<Date>(
    secondSailRef?.departureDate ? DateTime.fromISO(secondSailRef.departureDate).toJSDate() : new Date()
  )
  const [returnSailRefId, setReturnSailRefId] = useState(secondSailRef?.sailRefId ?? 0)
  const [returnTime, setReturnTime] = useState(secondSailRef?.departureTime ?? '')

  // Shared route state
  const [isSaving, setIsSaving] = useState(false)
  const [savingRoute, setSavingRoute] = useState<TripDirection | null>(null)

  // Debounce refs
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saveRouteChangesRef = useRef<() => Promise<ReservationWithModification | null>>(async () => null)

  // Computed
  const hasRouteChanges =
    outboundSailRefId !== (firstSailRef?.sailRefId ?? 0) || returnSailRefId !== (secondSailRef?.sailRefId ?? 0)
  const firstSailPackageSaved = hasSailPackageChanged(firstSailPackage, committedSailPackages)
  const secondSailPackageSaved = hasSailPackageChanged(secondSailPackage, committedSailPackages)

  const ticketSailRefId =
    outboundSailRefId !== 0 ? outboundSailRefId : (firstSailRef?.sailRefId ?? fallbackTicketSailRefId)
  const returnTicketSailRefId = returnSailRefId !== 0 ? returnSailRefId : secondSailRef?.sailRefId

  // Day click handlers
  const handleOutboundDayClick = useCallback((date: Date) => setOutboundDate(date), [])
  const handleReturnDayClick = useCallback((date: Date) => setReturnDate(date), [])

  const restoreOutboundSelection = useCallback(() => {
    if (!firstSailRef) return
    setOutboundSailRefId(firstSailRef.sailRefId)
    setOutboundTime(firstSailRef.departureTime)
  }, [firstSailRef])

  const restoreReturnSelection = useCallback(() => {
    if (!secondSailRef) return
    setReturnSailRefId(secondSailRef.sailRefId)
    setReturnTime(secondSailRef.departureTime)
  }, [secondSailRef])

  const resetToOriginal = useCallback(() => {
    restoreOutboundSelection()
    restoreReturnSelection()
    if (firstSailRef) setOutboundDate(DateTime.fromISO(firstSailRef.departureDate).toJSDate())
    if (secondSailRef) setReturnDate(DateTime.fromISO(secondSailRef.departureDate).toJSDate())
  }, [firstSailRef, secondSailRef, restoreOutboundSelection, restoreReturnSelection])

  // Ref+time auto-save handlers
  const setOutboundRefIdAndTime = useCallback((sailRefId: number, time: string, _isNextDay: boolean) => {
    setOutboundSailRefId(sailRefId)
    setOutboundTime(time)
    setSavingRoute('outbound')
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    autoSaveTimerRef.current = setTimeout(() => void saveRouteChangesRef.current(), 300)
  }, [])

  const setReturnRefIdAndTime = useCallback((sailRefId: number, time: string, _isNextDay: boolean) => {
    setReturnSailRefId(sailRefId)
    setReturnTime(time)
    setSavingRoute('return')
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    autoSaveTimerRef.current = setTimeout(() => void saveRouteChangesRef.current(), 300)
  }, [])

  // Inventory fetching
  const outboundDateISO = DateTime.fromJSDate(outboundDate).toISODate()
  const {
    inventory: outboundInventory,
    sailContent: outboundSailContent,
    isLoading: outboundInventoryLoading,
  } = useInventoryForDate({
    sailPackage: firstSailPackage?.code ?? '',
    date: outboundDateISO ?? '',
    locale,
  })

  const outboundModifiedInventory = useMemo(
    () =>
      getModifiedInventory({
        inventory: outboundInventory,
        nextInventory: defaultInventory,
        sailContent: outboundSailContent,
        nextSailContent: [],
      }),
    [outboundInventory, outboundSailContent]
  )

  const outboundRouteInfo: VoyageInfo = useMemo(() => {
    const duration =
      outboundSailContent.length > 0 && firstSailRef?.sailRefId
        ? getTravelDuration({ voyages: outboundSailContent, sailRefId: firstSailRef.sailRefId })
        : { hours: 0, minutes: 0 }
    return {
      departurePort: firstSailRef?.departureFrom ?? '',
      arrivalPort: firstSailRef?.arrivalTo ?? '',
      duration,
      shipName: '',
    }
  }, [firstSailRef, outboundSailContent])

  const outboundIsDayDisabled = useMemo(
    () => checkIsDayDisabled({ availableDates: availableDatesOutbound }),
    [availableDatesOutbound]
  )

  const returnDateISO = DateTime.fromJSDate(returnDate).toISODate()
  const {
    inventory: returnInventory,
    sailContent: returnSailContent,
    isLoading: returnInventoryLoading,
  } = useInventoryForDate({
    sailPackage: secondSailPackage?.code ?? '',
    date: returnDateISO ?? '',
    locale,
  })

  const returnModifiedInventory = useMemo(
    () =>
      getModifiedInventory({
        inventory: returnInventory,
        nextInventory: defaultInventory,
        sailContent: returnSailContent,
        nextSailContent: [],
      }),
    [returnInventory, returnSailContent]
  )

  const returnRouteInfo: VoyageInfo = useMemo(() => {
    const duration =
      returnSailContent.length > 0 && secondSailRef?.sailRefId
        ? getTravelDuration({ voyages: returnSailContent, sailRefId: secondSailRef.sailRefId })
        : { hours: 0, minutes: 0 }
    return {
      departurePort: secondSailRef?.departureFrom ?? '',
      arrivalPort: secondSailRef?.arrivalTo ?? '',
      duration,
      shipName: '',
    }
  }, [secondSailRef, returnSailContent])

  const returnIsDayDisabled = useMemo(
    () => checkIsDayDisabled({ availableDates: availableDatesReturn }),
    [availableDatesReturn]
  )

  // Save route changes
  const saveRouteChanges = useCallback(async (): Promise<ReservationWithModification | null> => {
    if (!reservationToken) return null

    setIsSaving(true)
    onError(null)

    const modifications = []

    if (outboundSailRefId !== (firstSailRef?.sailRefId ?? 0) && firstSailPackage && firstSailRef) {
      const dateISO = DateTime.fromJSDate(outboundDate).toISODate()
      modifications.push(
        buildUpdateSailPackagesModification({
          sailPackage: firstSailPackage.code,
          seqN: firstSailPackage.seqN ?? 1,
          currentSailRefIds: firstSailPackage.sailRefs.map((r) => r.sailRefId),
          newDepartureDate: dateISO ?? firstSailRef.departureDate,
          newDepartureTime: outboundTime,
          newSailRefId: outboundSailRefId,
          routeLeg: firstSailRef.routeLeg,
        })
      )
    }

    if (returnSailRefId !== (secondSailRef?.sailRefId ?? 0) && secondSailPackage && secondSailRef) {
      const dateISO = DateTime.fromJSDate(returnDate).toISODate()
      modifications.push(
        buildUpdateSailPackagesModification({
          sailPackage: secondSailPackage.code,
          seqN: secondSailPackage.seqN ?? 2,
          currentSailRefIds: secondSailPackage.sailRefs.map((r) => r.sailRefId),
          newDepartureDate: dateISO ?? secondSailRef.departureDate,
          newDepartureTime: returnTime,
          newSailRefId: returnSailRefId,
          routeLeg: secondSailRef.routeLeg,
        })
      )
    }

    if (modifications.length === 0) {
      setIsSaving(false)
      setSavingRoute(null)
      return currentReservation
    }

    let modifyResult: Awaited<ReturnType<typeof modify>>
    try {
      modifyResult = await modify(reservationToken, modifications, locale)
    } catch (err) {
      if (handleStaleServerAction({ error: err, getTranslation, onError: (message) => onError(message) })) {
        setIsSaving(false)
        setSavingRoute(null)
        return null
      }

      throw err
    }

    if (!modifyResult.success) {
      const outboundChanged = outboundSailRefId !== (firstSailRef?.sailRefId ?? 0)
      const returnChanged = returnSailRefId !== (secondSailRef?.sailRefId ?? 0)
      const changedRouteCount = (outboundChanged ? 1 : 0) + (returnChanged ? 1 : 0)
      const changedSailPackageSeqN =
        changedRouteCount === 1
          ? outboundChanged
            ? (firstSailPackage?.seqN ?? 1)
            : (secondSailPackage?.seqN ?? 2)
          : undefined
      const changedRouteHasVehicle =
        changedSailPackageSeqN !== undefined &&
        countReservationItems(currentReservation.items ?? [], changedSailPackageSeqN).vehicles > 0
      // The API does not identify the excluded item; use the vehicle copy only for
      // a leg carrying a vehicle until the response exposes the rejected category.
      const messageKey =
        modifyResult.error.code === MODIFY_ERROR_CODES.EXCLUDED_PRICE_CATEGORY && changedRouteHasVehicle
          ? 'vehicleCategoryExcluded'
          : (modifyResult.error.code && MODIFY_ERROR_MESSAGE_KEYS[modifyResult.error.code]) ||
            'commonNotificationErrorDesc'
      const errorMessage = formatActionError(getTranslation(messageKey), modifyResult.error.code, getTranslation)

      if (changedRouteCount === 1) {
        const routeName = outboundChanged ? (firstSailRef?.routeLegTitle ?? '') : (secondSailRef?.routeLegTitle ?? '')
        onError(`${routeName}: ${errorMessage}`)
      } else {
        onError(errorMessage)
      }

      if (outboundChanged) restoreOutboundSelection()
      if (returnChanged) restoreReturnSelection()
      setIsSaving(false)
      setSavingRoute(null)
      return null
    }

    // Update internal state from server response
    onReservationUpdated(modifyResult.data)

    const updatedPackages = modifyResult.data.sailPackages ?? []
    const newFirstSailRef = updatedPackages[0]?.sailRefs?.[0]
    const newSecondSailRef = updatedPackages[1]?.sailRefs?.[0]

    if (newFirstSailRef) {
      setOutboundSailRefId(newFirstSailRef.sailRefId)
      setOutboundTime(newFirstSailRef.departureTime)
      setOutboundDate(DateTime.fromISO(newFirstSailRef.departureDate).toJSDate())
    }

    if (newSecondSailRef) {
      setReturnSailRefId(newSecondSailRef.sailRefId)
      setReturnTime(newSecondSailRef.departureTime)
      setReturnDate(DateTime.fromISO(newSecondSailRef.departureDate).toJSDate())
    }

    setIsSaving(false)
    setSavingRoute(null)
    onError(null)

    return modifyResult.data
  }, [
    reservationToken,
    outboundSailRefId,
    outboundDate,
    outboundTime,
    returnSailRefId,
    returnDate,
    returnTime,
    firstSailPackage,
    firstSailRef,
    secondSailPackage,
    secondSailRef,
    currentReservation,
    locale,
    getTranslation,
    onReservationUpdated,
    onError,
    restoreOutboundSelection,
    restoreReturnSelection,
  ])

  // Keep ref always pointing to the latest saveRouteChanges to avoid stale closure in debounce
  useEffect(() => {
    saveRouteChangesRef.current = saveRouteChanges
  })

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    }
  }, [])

  return {
    outbound: {
      date: outboundDate,
      time: outboundTime,
      sailRefId: outboundSailRefId,
      routeInfo: outboundRouteInfo,
      modifiedInventory: outboundModifiedInventory,
      isInventoryLoading: outboundInventoryLoading,
      isDayDisabled: outboundIsDayDisabled,
      onDayClick: handleOutboundDayClick,
      setRefIdAndTime: setOutboundRefIdAndTime,
      isSaved: firstSailPackageSaved,
    },
    return: secondSailPackage
      ? {
          date: returnDate,
          time: returnTime,
          sailRefId: returnSailRefId,
          routeInfo: returnRouteInfo,
          modifiedInventory: returnModifiedInventory,
          isInventoryLoading: returnInventoryLoading,
          isDayDisabled: returnIsDayDisabled,
          onDayClick: handleReturnDayClick,
          setRefIdAndTime: setReturnRefIdAndTime,
          isSaved: secondSailPackageSaved,
        }
      : null,
    isSaving,
    savingRoute,
    hasRouteChanges,
    outboundSaved: firstSailPackageSaved,
    returnSaved: secondSailPackageSaved,
    saveRouteChanges,
    resetToOriginal,
    ticketSailRefId,
    returnTicketSailRefId,
  }
}
